import { useAtomValue } from "jotai";
import styles from "./styles.module.scss";
import { getUserGrade, userData, userProfile } from "../../state";
import { Link } from "react-router-dom";
import avatar from "../../assets/user.png";
import { ReactComponent as Tree } from "../../assets/chart-tree.svg";
import { ReactComponent as Search } from "../../assets/search-alt.svg";
import { ReactComponent as Message } from "../../assets/message.svg";
import { ReactComponent as CheckBox } from "../../assets/checkbox.svg";
import { ReactComponent as VerifiedBadge } from "../../assets/verified-badge.svg";
import { hasDateBeenReached } from "../../utils/formatDate";
import { MemberType } from "../../requests";
import { formatFullName, truncateString } from "../../utils/stringHelpers";
import { App, Button, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { MembershipBadge } from "../membershipBadge/membershipBadge";
import domtoimage from "dom-to-image-more";

const ProfileCard = ({
  addAboutLink,
  showLicenseScore,
  showUpgradeScore,
  isFinanciallyActive,
}: {
  addAboutLink?: boolean;
  showLicenseScore?: boolean;
  showUpgradeScore?: boolean;
  isFinanciallyActive?: boolean;
}) => {
  const { message } = App.useApp();
  const profile = useAtomValue(userProfile);
  const user = useAtomValue(userData);
  const [isDownloading, setIsDownloading] = useState(false);
  const badgeRef = useRef<HTMLDivElement>(null);
  const grade = getUserGrade(user?.MembershipTypeId!);
  const hasExpiredLicense = hasDateBeenReached(profile?.LicenseExpire!);
  const isCorporate = user?.MembershipTypeId === MemberType.Corporate;
  const fullName = formatFullName(
    profile?.LastName,
    profile?.FirstName,
    profile?.MiddleName,
  );

  useEffect(() => {
    if (isDownloading) {
      message.loading("Downloading membership badge...");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDownloading]);

  const handleBadgeDownload = async () => {
    if (!badgeRef.current) return;
    setIsDownloading(true);
    const dataUrl = await domtoimage.toPng(badgeRef.current, {
      scale: 3,
      bgcolor: "#000000",
    });
    const link = document.createElement("a");
    link.download = `Membership_Badge_${user?.MemberId}.png`;
    link.href = dataUrl;
    link.click();
    setIsDownloading(false);
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "status",
      label: "Financially Active",
      disabled: true,
      style: { fontWeight: 600, color: "#000" },
    },
    {
      type: "divider",
    },
    {
      key: "download",
      label: "Download Membership Badge",
      onClick: () => handleBadgeDownload(),
    },
  ];

  return (
    <div className={styles.largeGap}>
      <div
        style={{
          position: "fixed",
          top: "-9999px",
          left: "-9999px",
          pointerEvents: "none",
        }}
      >
        <MembershipBadge grade={grade} ref={badgeRef} />
      </div>
      <div className={styles.mediumGap}>
        <img src={profile?.UploadPictureUrl || avatar} alt="profile" />
        <div>
          <div className={styles.nameBadge}>
            {fullName?.length > 40 ? (
              <Tooltip title={fullName} placement="bottom">
                <h2>{truncateString(fullName, 40)}</h2>
              </Tooltip>
            ) : (
              <h2>{fullName}</h2>
            )}
            {isFinanciallyActive ? (
              <Dropdown menu={{ items: menuItems }} trigger={["hover"]}>
                <Button
                  type="text"
                  icon={<VerifiedBadge style={{ cursor: "pointer" }} />}
                />
              </Dropdown>
            ) : null}
          </div>
          {addAboutLink ? (
            <Link className={styles.extLink} to="/about-hrpl">
              About HRPL
            </Link>
          ) : null}
        </div>
      </div>
      <div className={styles.statsGroup}>
        <div className={styles.stat}>
          <div className={styles.icon1}>
            <Tree />
          </div>
          <section>
            <h4>{grade}</h4>
            <label>Grade</label>
          </section>
        </div>
        <div className={styles.stat}>
          <div className={styles.icon2}>
            <Search />
          </div>
          <section>
            <h4>{profile?.MembershipNo}</h4>
            <label>Number</label>
          </section>
        </div>
        <div className={styles.stat}>
          <div className={styles.icon3}>
            <Message />
          </div>
          {showLicenseScore ? (
            <section>
              <h4>{profile?.LicenseScore}</h4>
              <label>License Score</label>
            </section>
          ) : showUpgradeScore ? (
            <section>
              <h4>{profile?.UpgradeScore}</h4>
              <label>Upgrade Score</label>
            </section>
          ) : (
            <section>
              <h4>{profile?.LicenseScore + profile?.UpgradeScore}</h4>
              <label>Score</label>
            </section>
          )}
          {isCorporate ? null : (
            <div className={styles.stat}>
              <div className={styles.icon1}>
                <CheckBox />
              </div>
              <section>
                <h4>{hasExpiredLicense ? "Expired" : "Active"}</h4>
                <label>License Status</label>
              </section>
            </div>
          )}
        </div>
      </div>
      <div className={styles.membershipBadgeWrapper} ref={badgeRef}>
        <MembershipBadge grade={grade} />
      </div>
    </div>
  );
};

export default ProfileCard;
